import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container, Stack } from '@mui/system';
import { useEffect, useState } from 'react';

import ExtensionSubMenu from '@/components/nav/ExtensionSubMenu';
import Popup15397 from '@/components/ui/15397Popup';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';
// import type { LayoutWrapperContents } from '@/types/LayoutWrapperContents';

const Form15397 = () => {
  // const [open, setOpen] = useState(false);
  const [referralUrl, SetReferralUrl] = useState('form15397');

  // const layoutWrapperContents: LayoutWrapperContents = {
  //   Heading: `Running late on your tax-exempt organization's return? `,
  //   SubHeading:
  //     'Extend your deadline by e-filing Form 8809. File now and get the extra time you need.',
  //   CtaText: 'E-file Form 8809 Now',
  //   CtaSubHeading: `Get up to 6 months`,
  //   CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form8868sticky`,
  //   isActive: true,
  // };
  useEffect(() => {
    const isIRSPageReferral = document.referrer === 'https://www.irs.gov/';
    if (isIRSPageReferral) SetReferralUrl('form15397irs');
  }, []);
  const DEFAULT_ACTIVE_ID = 'f15397-step1-img';

  const [activeSectionId, setActiveSectionId] =
    useState<string>(DEFAULT_ACTIVE_ID);

  const getOpacity = (id: string) => (activeSectionId === id ? 1 : 0.5);
  const handleSectionClick = (id: string) => {
    setActiveSectionId(id);
  };

  return (
    <AppLayout
      // LayoutWrapperContents={layoutWrapperContents}
      meta={
        <Meta
          title="File Form 15397 | Extend W-2, 1099 & ACA Recipient Copies"
          description="File IRS Form 15397 to request a one-time 30-day extension to furnish W-2, 1099, ACA, and other information returns. Stay compliant with TaxZerone."
          keywords=""
          canonical="tax-extension/file-irs-form-15397-online/"
          jsonLdPricing="9.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
          ogShareImg="assets/images/extension-share-img.png"
        />
      }
    >
      <ExtensionSubMenu />
      <Popup15397 />
      <Container
        component="section"
        sx={{
          position: 'relative',
          mt: 2,
          mb: 0,
        }}
        maxWidth="lg"
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1d3557, #656d4a)',
            p: 1.5,
            px: 4,
            color: '#e9dddd',
          }}
          className="ribbon"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Typography
              variant="body1"
              sx={{
                textAlign: { xs: 'center', sm: 'left' },
                fontSize: { xs: 16, sm: 17, md: 20, lg: '1.25rem' },
              }}
            >
              <strong>📢 Need more time to provide recipient copies? </strong>{' '}
              File{' '}
              <span style={{ color: '#ccff33', fontWeight: 600 }}>
                Form 15397
              </span>{' '}
              now to request{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              a one-time 30-day extension.
            </Typography>
            <Box>
              <Button
                variant="contained"
                size="small"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form15397`}
                sx={{
                  backgroundColor: '#ffffff',
                  '&:hover, &:focus': {
                    backgroundColor: '#213e99',
                    color: '#fff',
                  },
                  px: 2,
                  color: '#333',
                  mt: { xs: 0, md: 0 },
                  display: { xs: 'inline-block', sm: 'inline-block' },
                  mx: { xs: 'auto', sm: 1 },
                  borderRadius: 3,
                  boxShadow: 'none',
                  fontSize: 18,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                File Now!
              </Button>
            </Box>
          </Stack>
        </Box>
      </Container>

      <Container component="section" maxWidth="xl">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ px: { xs: 2, md: 0 } }}
        >
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'left' },
              alignItems: 'center',
              display: 'flex',
              width: '100%',
              maxWidth: { xs: 'inherit', lg: 770 },
              minWidth: { xs: 'inherit', md: 530 },
              pl: { xs: 0, md: 6, lg: 8, xl: 12 },
              justifyContent: 'center',
            }}
          >
            <Box
              component="div"
              sx={{ mt: { xs: 2, sm: 4, md: 5 }, mb: { xs: 2, sm: 4, md: 5 } }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { lg: 40, md: 30, sm: 24, xs: 24 },
                  fontWeight: 600,
                  color: '#374151',
                  mb: 3,
                }}
              >
                File IRS{' '}
                <span className="highlight-container">
                  <span className="highlight">Form 15397</span>
                </span>{' '}
                Online for 2025 Tax Year
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                Easily request a{' '}
                <span style={{ fontWeight: 600 }}> 30-day extension</span> to
                furnish recipient copies of certain information returns. It
                covers Form W-2, 1099, 1098, 1095, and more.
              </Typography>
              <Alert
                severity="warning"
                id="who"
                icon={false}
                sx={{ mb: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
              >
                <b>Note:</b> TaxZerone will securely{' '}
                <span style={{ fontWeight: 600 }}> fax</span> your form on your
                behalf to the IRS - no mailing delays
              </Alert>
              <Stack
                direction={{ xs: 'column-reverse', md: 'row' }}
                spacing={{ xs: 1, md: 6 }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start',
                }}
                alignItems="center"
                sx={{ mt: 4 }}
              >
                <Box sx={{ textAlign: 'center', mr: { xs: 0, md: 4 } }}>
                  <Button
                    variant="contained"
                    className="btn-primary"
                    sx={{
                      transition: 'all 0.3s ease 0s',
                      '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                    }}
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                  >
                    Get Extension Now
                  </Button>
                  {/* <Typography
                    variant="body2"
                    sx={{ mt: 1, fontSize: { xs: 11, sm: 14 } }}
                  >
                    Complete in 3 easy steps.
                  </Typography> */}
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent={{
                    xs: 'center',
                    md: 'flex-start',
                  }}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      mb: { xs: '0 !important', md: '22px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/irs-authorized.webp" />
                      <source srcSet="/assets/images/irs-authorized.png" />
                      <Box
                        component="img"
                        src="/assets/images/irs-authorized.png"
                        loading="lazy"
                        alt="IRS-authorized e-file provider"
                        width={70}
                        height={69}
                        sx={{
                          width: { xs: 50, md: 70 },
                          height: { xs: 49, md: 69 },
                        }}
                      />
                    </picture>
                  </Box>
                  <Box
                    sx={{
                      mb: { xs: '0px !important', md: '22px !important' },
                    }}
                  >
                    <picture>
                      <source srcSet="/assets/images/tzo-soc2.webp" />
                      <source srcSet="/assets/images/tzo-soc2.png" />
                      <Box
                        component="img"
                        src="/assets/images/tzo-soc2.png"
                        loading="lazy"
                        alt="TZO SOC2 Compliant"
                        width={90}
                        height={89}
                        sx={{
                          width: { xs: 70, md: 90 },
                          height: { xs: 69, md: 89 },
                        }}
                      />
                    </picture>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Hidden mdDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                width: '100%',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/f15397-landing-img.webp"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <source
                  srcSet="/assets/images/f15397-landing-img.png"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
                <img
                  src="/assets/images/f15397-landing-img.png"
                  alt="File Extension Form 15397 Online for Tax Year 2025"
                  className="img-responsive-banner"
                  width={880}
                  height={725}
                />
              </picture>
            </Box>
          </Hidden>
          <Hidden mdUp smDown>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
                width: '100%',
                backgroundPosition: 'center center !important',
                mt: '0 !important',
              }}
            >
              <picture style={{ marginBottom: -7 }}>
                <source
                  srcSet="/assets/images/f15397-landing-img-sm.webp"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <source
                  srcSet="/assets/images/f15397-landing-img-sm.png"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
                <img
                  src="/assets/images/f15397-landing-img-sm.png"
                  alt="File Extension Form 15397 Online for Tax Year 2025"
                  className="img-responsive-banner"
                  width={440}
                  height={365}
                />
              </picture>
            </Box>
          </Hidden>
        </Stack>
      </Container>

      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container
          maxWidth="lg"
          sx={{
            my: { xs: 0, md: 3, lg: 5 },
            py: { xs: 2, sm: 3, md: 3, lg: 3 },
            borderRadius: { xs: 0, md: 4 },
            mx: { xs: 0, md: 3, lg: 'auto' },
          }}
          className="gradient-bg"
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h2"
                sx={{
                  mt: -1,
                  fontSize: { lg: 33, md: 28, sm: 24, xs: 20 },
                  fontWeight: 500,
                  mb: 0,
                  textAlign: 'left',
                  lineHeight: 1.3,
                  '& strong': {
                    color: '#ffe400',
                    fontSize: { xs: 24, sm: 30, md: 30, lg: 46 },
                  },
                }}
              >
                Running out of time to distribute your recipient copies?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#f3f3f3',
                  mb: 0,
                  textAlign: 'left',
                  '& b ': {
                    color: '#ffe400',
                    fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                  },
                }}
              >
                Get an online extension to meet your deadline and stay compliant
                with the IRS.
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100', sm: 180, md: 178 },
                textAlign: 'center',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  '& strong': {
                    color: '#ffe400',
                    fontSize: { xs: 24, sm: 30, md: 34, lg: 46 },
                  },
                }}
              >
                File for just
                <br />
                <strong>$9.99</strong>
              </Typography>

              {/* <Button
                variant="contained"
                size="large"
                href="/pricing/#extension"
                sx={{
                  backgroundColor: '#fff',
                  '&:hover, &:focus': {
                    backgroundColor: '#213e99',
                    color: '#fff',
                  },
                  px: 2,
                  color: '#333',
                  mt: { xs: 0, md: 0 },
                  display: { xs: 'inline-block', sm: 'inline-block' },
                  mx: { xs: 'auto', sm: 1 },
                  borderRadius: 3,
                  boxShadow: 'none',
                  fontSize: 18,
                  textTransform: 'none',
                }}
              >
                See Pricing
              </Button> */}
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <picture>
                  <source srcSet="/assets/images/f15397-application-img.webp" />
                  <source srcSet="/assets/images/f15397-application-img.png" />
                  <img
                    src="/assets/images/f15397-application-img.png"
                    alt="What is IRS Form 15397?"
                    className="img-responsive"
                    loading="lazy"
                    width={550}
                    height={500}
                  />
                </picture>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }} sx={{ mr: 0 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                What is IRS Form 15397?
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Form 15397 (Application for Extension of Time to Furnish
                Recipient Statements) allows issuers or transmitters to request
                a{' '}
                <span style={{ fontWeight: 600 }}>
                  {' '}
                  one-time{' '}
                  <Hidden smDown>
                    {' '}
                    <br />
                  </Hidden>{' '}
                  30-day extension
                </span>{' '}
                to distribute recipient copies of certain information returns.
              </Typography>
              <Alert
                severity="warning"
                icon={false}
                sx={{
                  mt: 3,

                  textAlign: 'left',
                  mb: 2,
                  borderLeft: '3px solid rgb(102, 60, 0)',
                }}
              >
                <b>Note:</b> File Form 15397 as soon as you know an extension of
                time is needed, but{' '}
                <span style={{ fontWeight: 600 }}> not before January 1st</span>
                . It applies only to recipient statements. To extend the IRS
                filing deadlines, use{' '}
                <Link
                  href="/resources/tax-extension/form-8809-instructions/"
                  sx={{ color: '#0202ff' }}
                >
                  {' '}
                  Form 8809
                </Link>
                .
              </Alert>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction={{ md: 'row', sm: 'row', xs: 'column-reverse' }}
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                Who Should File Form 15397?
              </Typography>

              <Typography variant="body1" sx={{ mb: 1 }}>
                You should file Form 15397 if you furnish recipient copies of
                information returns and need more time, including:
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyleType: 'none',
                  paddingLeft: 0,
                  textAlign: 'left',
                  ml: { xs: 0, sm: 3 },
                  mt: { xs: 0, md: 3 },
                  mb: { xs: 0, md: 3 },
                  '& li': { display: 'flex' },
                }}
              >
                {' '}
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>Employers issuing Forms W-2</Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>Businesses or payers furnishing 1099 forms</Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>
                    Financial institutions providing savings or contribution
                    statements
                  </Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>Health coverage providers furnishing ACA forms</Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>Trusts, estates, or other required issuers </Box>
                </li>
                <li
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                  <Box>
                    Transmitters unable to meet the recipient furnishing
                    deadline{' '}
                  </Box>
                </li>
              </Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                The request must be submitted by the original recipient due
                date.
              </Typography>
              {/* <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        sx={{
                          mt: 0,
                          transition: 'all 0.3s ease 0s',
                          '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                        }}
                        className="btn-primary"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1099da`}
                      
                      >
                        Stay Tuned for E-filing
                      </Button>
                    </Box> */}
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <picture>
                  <source srcSet="/assets/images/f15397-who-must-img.webp" />
                  <source srcSet="/assets/images/f15397-who-must-img.png" />
                  <img
                    src="/assets/images/f15397-who-must-img.png"
                    alt="Who Should File Form 15397?"
                    className="img-responsive-boir-img"
                    loading="lazy"
                    width={550}
                    height={500}
                  />
                </picture>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ mt: { xs: 2, sm: 6, md: 8 } }}
            className="MuiGrid-root-width"
          >
            <Grid
              size={12}
              textAlign="center"
              sx={{ justifyContent: 'center', alignItems: 'center' }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Which information returns are covered under Form 15397?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                The following forms are eligible for a deadline extension
              </Typography>
              <Box
                component="div"
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  // height: '100%',
                  // width: '100%',
                }}
              >
                <picture>
                  <source
                    srcSet="/assets/images/f15397-deadline-extension-img.webp"
                    className="img-responsive"
                    width={900}
                    height={500}
                  />
                  <source
                    srcSet="/assets/images/f15397-deadline-extension-img.png"
                    className="img-responsive"
                    width={900}
                    height={500}
                  />
                  <img
                    src="/assets/images/f15397-deadline-extension-img.png"
                    alt="The following forms are eligible for a deadline extension"
                    className="img-responsive"
                    loading="lazy"
                    width={900}
                    height={500}
                    style={{ borderRadius: 20 }}
                  />
                </picture>
              </Box>

              <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 3,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                >
                  File Now
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ mt: { xs: 2, sm: 6, md: 8 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                How to File Form 15397 in 3 simple steps with TaxZerone?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 0,
                  textAlign: 'center',
                }}
              >
                You can file the form online in minutes and request an extension
                by following these steps.
              </Typography>
            </Grid>
          </Grid>
        </Container>
        <Container maxWidth="xl" sx={{ mb: 5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: -3, md: -5, lg: -8 },
              }}
            >
              <picture>
                <source
                  srcSet="/assets/images/f15397-steps-img.webp"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <source
                  srcSet="/assets/images/f15397-steps-img.png"
                  className="img-responsive"
                  width={915}
                  height={790}
                />
                <img
                  src="/assets/images/f15397-steps-img.png"
                  alt="How to File Form 15397 in 3 simple steps with TaxZerone?"
                  className="img-responsive"
                  loading="lazy"
                  width={915}
                  height={790}
                />
              </picture>
            </Box>

            <Box
              sx={{
                width: '100%',
              }}
            >
              <Box
                component="ol"
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                  p: 2,
                  pl: 0,
                  ml: { xs: 0, sm: 0, md: -2 },
                }}
              >
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        my: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source
                          srcSet="/assets/images/step-1-icon.webp"
                          width={39}
                          height={39}
                        />
                        <source
                          srcSet="/assets/images/step-1-icon.png"
                          width={39}
                          height={39}
                        />
                        <img
                          src="/assets/images/step-1-icon.png"
                          alt="Enter Your Information"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Enter Your Information
                    </Typography>
                    <Typography variant="body1">
                      Enter the issuer’s/transmitter’s name, TIN, address, and
                      contact details.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        m: 0,
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-2-icon.webp" />
                        <source srcSet="/assets/images/step-2-icon.png" />
                        <img
                          src="/assets/images/step-2-icon.png"
                          alt="Select Forms for Extension"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Select Forms for Extension
                    </Typography>
                    <Typography variant="body1">
                      Choose the form to request an extension and briefly
                      explain the reason.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineDot
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        mt: -1,
                        mb: -1.7,
                      }}
                    >
                      <picture>
                        <source srcSet="/assets/images/step-3-icon.webp" />
                        <source srcSet="/assets/images/step-3-icon.png" />
                        <img
                          src="/assets/images/step-3-icon.png"
                          alt="Review & Transmit"
                          loading="lazy"
                          width={39}
                          height={39}
                        />
                      </picture>
                    </TimelineDot>
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                        fontWeight: 600,
                        mt: 0,
                        mb: 1,
                        color: '#36324a',
                      }}
                    >
                      Review & Transmit
                    </Typography>
                    <Typography variant="body1">
                      Review your details carefully. TaxZerone will securely fax
                      your form to the IRS.
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              </Box>

              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 3,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                >
                  Request an Extension Now
                </Button>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Grid>

      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        className="dotted-curve"
      >
        <Container maxWidth="lg" sx={{ mb: 5 }}>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            m={2}
            mt={5}
            pb={0}
            className="MuiGrid-root-width"
          >
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Why choose TaxZerone?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Explore the powerful features of TaxZerone today!
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2} m={2} mt={4} pb={{ xs: 0, sm: 4, md: 7 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/fast-filing-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/fast-filing-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/fast-filing-icon.png"
                      alt="Fast Filing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Fast Filing
                </Typography>
                <Typography variant="body1">
                  File your extension online in just a few minutes by following
                  our{' '}
                  <Link
                    href="/resources/tax-extension/form-15397-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    form 15397 instructions
                  </Link>
                  .
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/lowest-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/lowest-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/lowest-icon.png"
                      alt="Affordable Pricing"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Affordable Pricing
                </Typography>
                <Typography variant="body1">
                  TaxZerone offers clear, transparent pricing with zero hidden
                  charges. You can file without breaking the bank.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/review-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/review-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/review-icon.png"
                      alt="Smart Error Detection "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Smart Error Detection
                </Typography>
                <Typography variant="body1">
                  TaxZerone&apos;s built-in error check will review your data to
                  ensure your form doesn’t have any error and filed accurately.
                </Typography>
              </Paper>
            </Grid>
            <Hidden mdDown>
              <Grid size={12}>&nbsp;</Grid>
            </Hidden>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/zeronevault-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/zeronevault-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/zeronevault-icon.png"
                      alt="ZeroneVault"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  ZeroneVault
                </Typography>
                <Typography variant="body1">
                  You can easily share the recipient copies through ZeroneVault.
                  It will allow you to send files securely.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  borderColor: '#8492c0',
                  borderRadius: 10,
                  minHeight: '100%',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/free-efile-retransmission-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/free-efile-retransmission-icon.png"
                      alt="Free Retransmission "
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Free Retransmission
                </Typography>
                <Typography variant="body1">
                  If your extension request is ever rejected, you can retransmit
                  it at no additional cost until the Internal Revenue Service
                  accepts it.
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  py: 5,
                  textAlign: 'center',
                  borderWidth: 3,
                  minHeight: '100%',
                  borderColor: '#76ce97',
                  borderRadius: 10,
                  mt: { xs: 0, md: 4 },
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '50%',
                    width: 90,
                    height: 90,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <picture style={{ width: 60, height: 60 }}>
                    <source
                      srcSet="/assets/images/support-icon.webp"
                      width={60}
                      height={60}
                    />
                    <source
                      srcSet="/assets/images/support-icon.png"
                      width={60}
                      height={60}
                    />
                    <img
                      src="/assets/images/support-icon.png"
                      alt="Dedicated Customer Support"
                      loading="lazy"
                      width={60}
                      height={60}
                    />
                  </picture>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    fontWeight: 600,
                    my: 3,
                  }}
                >
                  Dedicated Customer Support
                </Typography>
                <Typography variant="body1">
                  Our support team is always there to help you with any
                  questions or filing issues or guidance throughout the process.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ mt: { xs: 2, sm: 6, md: 8 } }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                What Information Is Required to Complete IRS Form 15397?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 0,
                  textAlign: 'center',
                }}
              >
                To request an extension, make sure you have the following
                details ready:
              </Typography>
            </Grid>
          </Grid>
        </Container>
        <Container maxWidth="xl" sx={{ mb: 5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                mt: { xs: -3, md: -5, lg: -8 },
              }}
            >
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                  // mt: { xs: -3, md: -5, lg: -8 },
                }}
              >
                <img
                  src={`/assets/images/${activeSectionId}.webp`}
                  alt="Who Should File Form 5498s?"
                  className="img-responsive"
                  loading="lazy"
                  width={850}
                  height={469}
                  style={{ borderRadius: 21 }}
                />
              </Box>
            </Box>

            <Box sx={{ width: '100%' }}>
              <Box
                component="div"
                onClick={() => handleSectionClick('f15397-step1-img')}
                sx={{
                  cursor: 'pointer',
                  opacity: getOpacity('f15397-step1-img'),
                  transition: 'opacity 0.2s ease-in-out',
                  mb: 2,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: '#eff3ff',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      fontWeight: 600,
                    }}
                  >
                    1. Issuer’s or Transmitter’s Information
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      paddingLeft: 0,
                      textAlign: 'left',
                      ml: { xs: 0, sm: 3 },
                      mt: { xs: 0, md: 3 },
                      mb: { xs: 0, md: 0 },
                      '& li': { display: 'flex' },
                    }}
                  >
                    {' '}
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>Name </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>Employer Identification Number (EIN) </Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>Mailing address</Box>
                    </li>
                    <li
                      style={{
                        marginBottom: 10,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{ color: '#1da94a', mr: 1 }}
                      />{' '}
                      <Box>Contact number </Box>
                    </li>
                  </Box>
                </Paper>
              </Box>
              <Box
                component="div"
                onClick={() => handleSectionClick('f15397-step2-img')}
                sx={{
                  cursor: 'pointer',
                  opacity: getOpacity('f15397-step2-img'),
                  transition: 'opacity 0.2s ease-in-out',
                  mb: 2,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 3, backgroundColor: '#eafbff' }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    2. Form(s) Requiring an Extension
                  </Typography>
                  <Typography variant="body1">
                    Select the specific information return(s) for which you are
                    requesting additional time.
                  </Typography>
                </Paper>
              </Box>
              <Box
                component="div"
                onClick={() => handleSectionClick('f15397-step3-img')}
                sx={{
                  cursor: 'pointer',
                  opacity: getOpacity('f15397-step3-img'),
                  transition: 'opacity 0.2s ease-in-out',
                  mb: 2,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 3, backgroundColor: '#fbfff3' }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      fontWeight: 600,
                    }}
                  >
                    3. Reason for the Extension Request
                  </Typography>
                  <Typography variant="body1">
                    Briefly explain why you need extra time to furnish recipient
                    copies.
                  </Typography>
                </Paper>
              </Box>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 4,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
                >
                  File Now
                </Button>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ my: { xs: 2, sm: 6, md: 8 } }}
            className="MuiGrid-root-width"
          >
            <Grid
              size={12}
              textAlign="center"
              sx={{ justifyContent: 'center', alignItems: 'center' }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Common Mistakes to Avoid When Filing Form 15397
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Even small errors can lead to IRS rejection or delays in
                approving your extension request.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  borderRadius: 3,
                  minHeight: '100%',
                  mb: 0,
                  borderLeft: '5px solid #ff4d6d',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box sx={{ display: 'flex', ml: -2 }}>
                  ❌
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 19, sm: 18, xs: 16 },
                        mb: 1,
                      }}
                    >
                      Incorrect or missing TIN
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      Providing wrong or incomplete Taxpayer Identification
                      Numbers (TIN) of issuers or transmitters.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>{' '}
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  borderRadius: 3,
                  minHeight: '100%',
                  mb: 0,
                  borderLeft: '5px solid #ff4d6d',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box sx={{ display: 'flex', ml: -2 }}>
                  ❌
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 19, sm: 18, xs: 16 },
                        mb: 1,
                      }}
                    >
                      Not attaching the required list
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      When requesting an extension for multiple issuers or
                      transmitters, failing to attach a typed list of all names
                      along with their TINs.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  borderRadius: 3,
                  minHeight: '100%',
                  mb: 0,
                  borderLeft: '5px solid #ff4d6d',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box sx={{ display: 'flex', ml: -2 }}>
                  ❌
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 19, sm: 18, xs: 16 },
                        mb: 1,
                      }}
                    >
                      Missing signature
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      The IRS will not accept the request if the form is
                      unsigned by an authorized person.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  borderRadius: 3,
                  minHeight: '100%',
                  mb: 0,
                  borderLeft: '5px solid #ff4d6d',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box sx={{ display: 'flex', ml: -2 }}>
                  ❌
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 19, sm: 18, xs: 16 },
                        mb: 1,
                      }}
                    >
                      Entering a total when filing separate forms
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      If you file a separate Form 15397 for each issuer, do not
                      enter the total number of issuers on the form.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  borderRadius: 3,
                  minHeight: '100%',
                  mb: 0,
                  borderLeft: '5px solid #ff4d6d',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box sx={{ display: 'flex', ml: -2 }}>
                  ❌
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 19, sm: 18, xs: 16 },
                        mb: 1,
                      }}
                    >
                      Assuming the Extension Applies to IRS Filing
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      This form only extends the time to furnish statements to
                      recipients, not the time to file information returns with
                      the IRS.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  borderRadius: 3,
                  minHeight: '100%',
                  mb: 0,
                  borderLeft: '5px solid #ff4d6d',
                  backgroundColor: '#f0f2f9',
                }}
              >
                <Box sx={{ display: 'flex', ml: -2 }}>
                  ❌
                  <Box sx={{ ml: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 19, sm: 18, xs: 16 },
                        mb: 1,
                      }}
                    >
                      Filing Too Early
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      Do not file the form before January 1st of the tax year,
                      even if an extension is needed.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#f0f2f9',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
          flexDirection: 'column',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={2}
            sx={{ my: { xs: 2, sm: 6, md: 8 } }}
            className="MuiGrid-root-width"
          >
            <Grid
              size={12}
              textAlign="center"
              sx={{ justifyContent: 'center', alignItems: 'center' }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: { xs: 1, sm: 3 },
                }}
              >
                IRS Form 15397 vs Form 8809
              </Typography>
            </Grid>
            <Grid size={12}>
              <Box component="div" sx={{ mb: 2, maxWidth: 700, m: 'auto' }}>
                <TableContainer
                  sx={{ border: '1px solid #828282', borderRadius: 2 }}
                >
                  <Table sx={{}} aria-label="Due Date Chart">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#ccc' }}>
                        <TableCell
                          sx={{
                            color: '#444',
                            fontWeight: 600,
                            width: 300,
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            fontSize: 17,
                          }}
                        >
                          Comparison
                        </TableCell>

                        <TableCell
                          sx={{
                            color: '#444',
                            fontWeight: 600,
                            width: 300,
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            fontSize: 17,
                          }}
                        >
                          Form 15397
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#444',
                            fontWeight: 600,
                            width: 300,
                            fontSize: 17,
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                          }}
                        >
                          Form 8809
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                            fontSize: 17,
                            fontWeight: 600,
                          }}
                        >
                          Purpose
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          Extend time to furnish recipient copies
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          Extend time to file with IRS/SSA
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 17,
                            fontWeight: 600,
                          }}
                        >
                          Applies To
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          Employees and payees
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            borderBottom: '1px solid #828282',
                            fontSize: 16,
                          }}
                        >
                          IRS or SSA
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 17,
                            fontWeight: 600,
                          }}
                        >
                          Extension Length
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          One-time 30 days
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            borderBottom: '1px solid #828282',
                            fontSize: 16,
                          }}
                        >
                          30 days (second extension limited)
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontWeight: 600,
                            fontSize: 17,
                          }}
                        >
                          Covers Recipient Deadlines
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',
                            borderBottom: '1px solid #828282',
                            textAlign: 'center',
                            backgroundColor: '#fff',
                          }}
                        >
                          Yes
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            borderBottom: '1px solid #828282',
                          }}
                        >
                          No
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',

                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontWeight: 600,
                            fontSize: 17,
                          }}
                        >
                          Filing Method
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: '1px solid #828282',

                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          Online and fax
                        </TableCell>
                        <TableCell
                          sx={{
                            textAlign: 'center',
                            backgroundColor: '#fff',
                            fontSize: 16,
                          }}
                        >
                          E-file or Paper
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Grid
        component="section"
        container
        sx={{
          backgroundColor: '#fff',
          mx: '0 !important',
          width: '100% !important',
          backgroundPosition: 'center bottom',
        }}
        spacing={2}
        m={0}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Grid
            container
            spacing={2}
            m={2}
            mt={{ xs: 5, sm: 5, md: 6 }}
            mb={{ xs: 5, sm: 5, md: 8 }}
            className="MuiGrid-root-width"
          >
            <Grid size={12} textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: { xs: 1, sm: 3 },
                }}
              >
                Download Official IRS Form 15397 and Instructions
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: { md: 22, sm: 20, xs: 18 }, mb: 2 }}
              >
                Access the IRS forms directly:
              </Typography>
              <Stack
                spacing={2}
                direction={{ xs: 'column', sm: 'row', md: 'row' }}
                sx={{ textAlign: 'center', justifyContent: 'center' }}
              >
                <Link
                  href="https://www.irs.gov/pub/irs-pdf/f15397.pdf"
                  target="_blank"
                  sx={{ textDecoration: 'none' }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 3, sm: 2, md: 2 },
                      borderRadius: 3,
                      display: 'grid',
                      gridTemplateColumns: 'auto auto ',
                      columnGap: 2,
                      placeContent: 'start',
                      backgroundColor: '#f0f2f9',
                      transition: 'all 0.3s ease-in-out',
                      textDecoration: 'none',
                      // cursor: 'pointer',
                      // width: { md: 'auto', sm: '250px !important', xs: 'auto' },
                      maxWidth: '100%',
                      alignSelf: 'center',
                      cursor: 'pointer',

                      '&:hover': {
                        boxShadow: 'rgba(0, 0, 0, 0.13) 0px 0px 15px 0px',
                        transition: '0.3s',
                      },
                    }}
                  >
                    <PictureAsPdfOutlinedIcon sx={{ fontSize: 30 }} />

                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 400, fontSize: 18 }}
                    >
                      Download Form 15397 PDF
                    </Typography>
                  </Paper>
                </Link>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Box
        component="section"
        sx={{
          backgroundColor: '#1da94a',
          py: { lg: 8, md: 8, sm: 4, xs: 4 },
          mx: '0 !important',
          width: '100% !important',
          mb: 0,
          p: 2,
          textAlign: 'center',
        }}
        m={2}
        mt={0}
        className="curve-bg"
      >
        <Typography
          variant="h2"
          color="white"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Ready to extend your information return recipient copy deadline?
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            color: '#f3f3f3',
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          File with TaxZerone today for easy filing and faster processing.
        </Typography>
        <Box component="div" sx={{ margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{
              mt: { xs: 0, sm: 4 },
              mb: { xs: 0, md: 0 },
              color: '#444',
              transition: 'all 0.3s ease 0s',
              '&:hover, &active': {
                backgroundColor: '#213e99 !important',
                color: '#fff',
                transform: 'scale(1.1)',
              },
            }}
            className="btn-secondary-white"
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=${referralUrl}`}
          >
            Get an Extension Now
          </Button>
        </Box>
      </Box>
      <Container component="section" maxWidth="lg">
        <Grid
          container
          spacing={2}
          m={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 2 } }}
        >
          <Grid
            size={{ xs: 12, md: 9, sm: 10 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. Can I file Form 8809 instead of Form 15397?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      No, you cannot{' '}
                      <Link
                        href="/tax-extension/efile-irs-form-8809-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        file Form 8809
                      </Link>{' '}
                      instead of Form 15397. They both serve different purposes:
                    </Typography>
                    <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                      <li>
                        <Link
                          href="/resources/tax-extension/form-8809-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Form 8809
                        </Link>{' '}
                        (Extension of Time to File Information Returns) gives
                        you additional time to submit information return forms
                        to the IRS.{' '}
                      </li>
                      <li>
                        <Link
                          href="/resources/tax-extension/form-15397-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Form 15397
                        </Link>{' '}
                        (Extension of Time to Furnish Recipient Statements)
                        gives you additional time to send copies of the forms to
                        employees or recipients.{' '}
                      </li>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. When is the deadline to file Form 15397?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text" sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Form 15397 must be filed on or before the original due
                      date of the return for which you are requesting an
                      extension, but not earlier than January 1.
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '18px !important',
                      }}
                    >
                      Example:{' '}
                    </Typography>
                    <Typography variant="body1">
                      If you need additional time to furnish Form W-2 recipient
                      copies, you must file Form 15397 on or before{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        February 2, 2026.
                      </span>
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. How to file an extension Form 15397?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text" sx={{ mt: 1 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      You can file an extension{' '}
                      <Link
                        href="https://www.irs.gov/forms-pubs/mobile-friendly-forms"
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        online
                      </Link>{' '}
                      or fax it to the given address:
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        mb: 2,
                        // borderRadius: '20px',
                        clipPath:
                          'polygon(10px 0%, calc(100% - 10px) 0%, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0% calc(100% - 10px), 0% 10px )',
                        background:
                          'linear-gradient(63deg,rgba(211, 245, 232, 1) 0%, rgba(159, 214, 252, 1) 100%)',
                        maxWidth: 500,
                        border: '2px solid #0f0f0f',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        Internal Revenue Service Technical Services
                        Operation{' '}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <span style={{ fontWeight: 600 }}> Attn:</span>{' '}
                        Extension of Time Coordinator
                      </Typography>
                      <Typography variant="body1">
                        877-477-0572{' '}
                        <span style={{ fontWeight: 600 }}>
                          (International: 304-579-4105)
                        </span>
                      </Typography>
                    </Box>
                    <Alert
                      severity="warning"
                      id="who"
                      icon={false}
                      sx={{ mb: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                    >
                      <b>Note:</b>{' '}
                      <Link href="/" sx={{ color: '#0202ff' }}>
                        {' '}
                        TaxZerone
                      </Link>{' '}
                      makes sure your form is faxed to the IRS promptly and on
                      time.
                    </Alert>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. What reasons are accepted by the IRS for requesting a Form
                  15397 extension?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text" sx={{ mt: 1 }}>
                    <Typography variant="body1">
                      The IRS may accept the following reasons:
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        listStyleType: 'none',
                        paddingLeft: 0,
                        textAlign: 'left',
                        ml: { xs: 0, sm: 0 },
                        mt: { xs: 0, md: 1 },
                        mb: { xs: 0, sm: 6, md: 2 },
                        '& li': { display: 'flex' },
                      }}
                    >
                      {' '}
                      <li
                        style={{
                          marginBottom: 10,
                        }}
                      >
                        <CheckCircleRoundedIcon
                          sx={{ color: '#1da94a', mr: 1 }}
                        />{' '}
                        <Box>
                          Natural disasters, fires, or other catastrophic events
                          that disrupt business operations.
                        </Box>
                      </li>
                      <li
                        style={{
                          marginBottom: 10,
                        }}
                      >
                        <CheckCircleRoundedIcon
                          sx={{ color: '#1da94a', mr: 1 }}
                        />{' '}
                        <Box>Illness, death or other unavoidable causes.</Box>
                      </li>
                      <li
                        style={{
                          marginBottom: 10,
                        }}
                      >
                        <CheckCircleRoundedIcon
                          sx={{ color: '#1da94a', mr: 1 }}
                        />{' '}
                        <Box>
                          Missing or late Schedule 1 or other input information
                        </Box>
                      </li>
                      <li
                        style={{
                          marginBottom: 10,
                        }}
                      >
                        <CheckCircleRoundedIcon
                          sx={{ color: '#1da94a', mr: 1 }}
                        />{' '}
                        <Box>
                          Major technical breakdowns that make it impossible to
                          distribute statements on time
                        </Box>
                      </li>
                      <li
                        style={{
                          marginBottom: 0,
                        }}
                      >
                        <CheckCircleRoundedIcon
                          sx={{ color: '#1da94a', mr: 1 }}
                        />{' '}
                        <Box>
                          First-year business start-up challenges affecting
                          preparation timelines
                        </Box>
                      </li>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. How long is the extension granted under Form 15397?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text" sx={{ mt: 1 }}>
                    <Typography>
                      The IRS generally grants a{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        single-time 30-day extension
                      </span>{' '}
                      for furnishing recipient statements. This extension
                      provides additional time to ensure all statements are
                      prepared and distributed accurately.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  borderWidth: 2,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover, &:focus, &:active': { borderWidth: 2 },
                }}
                href="/faqs/form-15397/"
              >
                More FAQs
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form15397;
